
import { Link } from 'react-router-dom';

interface Business {
  id: number;
  name: string;
  tagline: string;
  category: string;
  description: string;
  address: string;
  phone: string;
  website: string;
  image: string;
  logo: string;
  rating: number;
  reviews: number;
  featured: boolean;
  verified: boolean;
  offer: string;
  openNow: boolean;
  hours: string;
  priceRange: string;
  yearEstablished: number;
  lastVerified?: string;
}

interface BusinessCardProps {
  business: Business;
}

export default function BusinessCard({ business }: BusinessCardProps) {
  return (
    <article
      className={`bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border group ${
        business.featured ? 'border-amber-200 ring-1 ring-amber-100' : 'border-gray-100'
      }`}
    >
      <div className="relative h-48 overflow-hidden">
        <img
          src={business.image}
          alt={`${business.name} - local business Roseville California`}
          title={`${business.name} Roseville CA`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>

        {/* Badges */}
        <div className="absolute top-3 left-3 flex items-center gap-2">
          {business.featured && (
            <span className="px-2.5 py-1 bg-amber-500 text-white text-xs font-bold rounded-full flex items-center gap-1 whitespace-nowrap">
              <i className="ri-star-fill text-xs"></i>
              Featured
            </span>
          )}
          {business.verified && (
            <span className="px-2.5 py-1 bg-teal-600 text-white text-xs font-bold rounded-full flex items-center gap-1 whitespace-nowrap">
              <i className="ri-verified-badge-fill text-xs"></i>
              Verified
            </span>
          )}
        </div>

        {/* Offer */}
        {business.offer && (
          <div className="absolute bottom-3 left-3">
            <span className="px-3 py-1.5 bg-white/95 backdrop-blur-sm text-teal-700 text-xs font-bold rounded-lg whitespace-nowrap">
              {business.offer}
            </span>
          </div>
        )}
      </div>

      <div className="p-5">
        {/* Header */}
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 bg-teal-50 rounded-xl flex items-center justify-center flex-shrink-0">
            <i className={`${business.logo} text-teal-600 text-lg`}></i>
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h4 className="text-base font-bold text-gray-900 group-hover:text-teal-600 transition-colors truncate">
                <Link to={`/directory/${business.id}`} className="cursor-pointer">
                  {business.name}
                </Link>
              </h4>
              <span className="text-xs text-gray-400 font-medium whitespace-nowrap">{business.priceRange}</span>
            </div>
            <p className="text-xs text-gray-500 italic truncate">{business.tagline}</p>
          </div>
        </div>

        {/* Description */}
        <p className="text-sm text-gray-600 mb-3 line-clamp-2 leading-relaxed">{business.description}</p>

        {/* Info */}
        <div className="space-y-1.5 mb-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
              <i className="ri-map-pin-line text-teal-500 text-sm"></i>
            </div>
            <span className="truncate">{business.address}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
              <i className="ri-phone-line text-teal-500 text-sm"></i>
            </div>
            <a href={`tel:${business.phone}`} className="hover:text-teal-600 cursor-pointer">{business.phone}</a>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
              <i className="ri-time-line text-teal-500 text-sm"></i>
            </div>
            <span className="truncate text-xs">{business.hours}</span>
          </div>
        </div>

        {/* Last Verified Badge */}
        {business.lastVerified && (
          <div className="mb-4">
            <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium ${
              business.verified
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                : 'bg-gray-50 text-gray-500 border border-gray-200'
            }`}>
              <i className={`ri-checkbox-circle-line text-xs ${business.verified ? 'text-emerald-500' : 'text-gray-400'}`}></i>
              Last verified: {business.lastVerified}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div className="flex items-center gap-1.5">
            <div className="flex items-center gap-0.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <i
                  key={i}
                  className={`text-xs ${
                    i < Math.floor(business.rating)
                      ? 'ri-star-fill text-amber-400'
                      : i < business.rating
                      ? 'ri-star-half-fill text-amber-400'
                      : 'ri-star-line text-gray-300'
                  }`}
                ></i>
              ))}
            </div>
            <span className="text-sm font-semibold text-gray-900">{business.rating}</span>
            <span className="text-xs text-gray-400">({business.reviews})</span>
          </div>

          <div className="flex gap-1.5">
            <a
              href={`tel:${business.phone}`}
              className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-teal-50 flex items-center justify-center transition-colors cursor-pointer"
              title="Call"
            >
              <i className="ri-phone-line text-gray-600 hover:text-teal-600 text-sm"></i>
            </a>
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(business.name + ' ' + business.address)}`}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-teal-50 flex items-center justify-center transition-colors cursor-pointer"
              title="Get Directions"
            >
              <i className="ri-navigation-line text-gray-600 hover:text-teal-600 text-sm"></i>
            </a>
            <Link
              to={`/directory/${business.id}`}
              className="w-8 h-8 rounded-lg bg-teal-50 hover:bg-teal-100 flex items-center justify-center transition-colors cursor-pointer"
              title="View Details"
            >
              <i className="ri-arrow-right-line text-teal-600 text-sm"></i>
            </Link>
          </div>
        </div>

        {/* Source Citation */}
        <div className="mt-3 pt-3 border-t border-gray-50">
          <p className="text-[10px] text-gray-400 leading-relaxed">
            Business information verified by owner • Ratings aggregated from customer reviews • Est. {business.yearEstablished}
          </p>
        </div>
      </div>
    </article>
  );
}
